import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Divider,
  Typography,
  Row,
  Col,
  Table,
  Modal,
  Switch,
} from 'antd';
import {
  CustomerServiceOutlined,
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api, { get } from '../../utils/api';
import { color } from 'echarts';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CustomerServiceConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [whatsappList, setWhatsappList] = useState([]);
  const [telegramList, setTelegramList] = useState([]);
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [telegramModalVisible, setTelegramModalVisible] = useState(false);
  const [editingWhatsapp, setEditingWhatsapp] = useState(null);
  const [editingTelegram, setEditingTelegram] = useState(null);
  const [contactForm] = Form.useForm();

  useEffect(() => {
    fetchConfig();
  }, []);

  // 获取配置
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await get('/admin/configs/customer-service');
      if (res.code === 200 && res.data) {
        const { customerServiceUrl, customerServiceWhatsapp, customerServiceTelegram } = res.data;
        
        form.setFieldsValue({
          customerServiceUrl: customerServiceUrl || '',
        });

        // 解析 WhatsApp 和 Telegram 配置
        if (customerServiceWhatsapp) {
          try {
            const parsed = typeof customerServiceWhatsapp === 'string' 
              ? JSON.parse(customerServiceWhatsapp) 
              : customerServiceWhatsapp;
            setWhatsappList(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            console.error('解析 WhatsApp 配置失败:', e);
            setWhatsappList([]);
          }
        }

        if (customerServiceTelegram) {
          try {
            const parsed = typeof customerServiceTelegram === 'string' 
              ? JSON.parse(customerServiceTelegram) 
              : customerServiceTelegram;
            setTelegramList(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            console.error('解析 Telegram 配置失败:', e);
            setTelegramList([]);
          }
        }
      }
    } catch (error) {
      message.error('获取配置失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 保存配置
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // 保存客服链接
      await api.put('/admin/configs/customer_service_url', {
        configValue: values.customerServiceUrl || '',
      });

      // 保存 WhatsApp 配置
      await api.put('/admin/configs/customer_service_whatsapp', {
        configValue: JSON.stringify(whatsappList),
      });

      // 保存 Telegram 配置
      await api.put('/admin/configs/customer_service_telegram', {
        configValue: JSON.stringify(telegramList),
      });

      message.success('配置保存成功');
    } catch (error) {
      message.error(error.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  // WhatsApp 表格列
  const whatsappColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record, index) => (
        <Switch 
          checked={status === '1' || status === 1} 
          checkedChildren="启用" 
          unCheckedChildren="禁用"
          onChange={(checked) => handleToggleWhatsappStatus(index, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record, index) => (
        <Space>
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditWhatsapp(record, index)}
          >
            编辑
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteWhatsapp(index)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // Telegram 表格列
  const telegramColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record, index) => (
        <Switch 
          checked={status === '1' || status === 1} 
          checkedChildren="启用" 
          unCheckedChildren="禁用"
          onChange={(checked) => handleToggleTelegramStatus(index, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record, index) => (
        <Space>
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTelegram(record, index)}
          >
            编辑
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteTelegram(index)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 添加 WhatsApp
  const handleAddWhatsapp = () => {
    setEditingWhatsapp(null);
    contactForm.resetFields();
    contactForm.setFieldsValue({ status: true });
    setWhatsappModalVisible(true);
  };

  // 编辑 WhatsApp
  const handleEditWhatsapp = (record, index) => {
    setEditingWhatsapp({ ...record, index });
    contactForm.setFieldsValue({
      name: record.name,
      url: record.url,
      status: record.status === '1' || record.status === 1,
    });
    setWhatsappModalVisible(true);
  };

  // 删除 WhatsApp
  const handleDeleteWhatsapp = (index) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个 WhatsApp 联系方式吗？',
      onOk: () => {
        const newList = whatsappList.filter((_, i) => i !== index);
        setWhatsappList(newList);
        message.success('删除成功');
      },
    });
  };

  // 切换 WhatsApp 状态
  const handleToggleWhatsappStatus = (index, checked) => {
    const newList = [...whatsappList];
    newList[index] = {
      ...newList[index],
      status: checked ? '1' : '0',
    };
    setWhatsappList(newList);
    message.success(checked ? '已启用' : '已禁用');
  };

  // 保存 WhatsApp
  const handleSaveWhatsapp = (values) => {
    const data = {
      name: values.name,
      url: values.url,
      status: values.status ? '1' : '0',
    };
    
    if (editingWhatsapp !== null && editingWhatsapp.index !== undefined) {
      // 编辑
      const newList = [...whatsappList];
      newList[editingWhatsapp.index] = data;
      setWhatsappList(newList);
      message.success('修改成功');
    } else {
      // 新增
      setWhatsappList([...whatsappList, data]);
      message.success('添加成功');
    }
    setWhatsappModalVisible(false);
  };

  // 添加 Telegram
  const handleAddTelegram = () => {
    setEditingTelegram(null);
    contactForm.resetFields();
    contactForm.setFieldsValue({ status: true });
    setTelegramModalVisible(true);
  };

  // 编辑 Telegram
  const handleEditTelegram = (record, index) => {
    setEditingTelegram({ ...record, index });
    contactForm.setFieldsValue({
      name: record.name,
      url: record.url,
      status: record.status === '1' || record.status === 1,
    });
    setTelegramModalVisible(true);
  };

  // 删除 Telegram
  const handleDeleteTelegram = (index) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个 Telegram 联系方式吗？',
      onOk: () => {
        const newList = telegramList.filter((_, i) => i !== index);
        setTelegramList(newList);
        message.success('删除成功');
      },
    });
  };

  // 切换 Telegram 状态
  const handleToggleTelegramStatus = (index, checked) => {
    const newList = [...telegramList];
    newList[index] = {
      ...newList[index],
      status: checked ? '1' : '0',
    };
    setTelegramList(newList);
    message.success(checked ? '已启用' : '已禁用');
  };

  // 保存 Telegram
  const handleSaveTelegram = (values) => {
    const data = {
      name: values.name,
      url: values.url,
      status: values.status ? '1' : '0',
    };
    
    if (editingTelegram !== null && editingTelegram.index !== undefined) {
      // 编辑
      const newList = [...telegramList];
      newList[editingTelegram.index] = data;
      setTelegramList(newList);
      message.success('修改成功');
    } else {
      // 新增
      setTelegramList([...telegramList, data]);
      message.success('添加成功');
    }
    setTelegramModalVisible(false);
  };

  return (
    <Card
      title={
        <Space>
          <CustomerServiceOutlined />
          <span>客服配置管理  (修改后要点击保存配置按钮) </span>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
          >
            刷新
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={submitting}
            onClick={() => form.submit()}
          >
            保存配置
          </Button>
        </Space>
      }
      loading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* 客服链接 */}
        <Title level={5}>在线客服链接</Title>
        <Paragraph type="secondary">
          配置在线客服系统的访问链接，用户点击"在线客服"按钮时会跳转到此链接
        </Paragraph>
        
        <Form.Item
          label="客服链接"
          name="customerServiceUrl"
          extra="例如：https://chat.example.com 或第三方客服系统链接"
        >
          <Input 
            placeholder="请输入客服链接" 
            prefix="🔗"
          />
        </Form.Item>

        <Divider />

        {/* WhatsApp 配置 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5}>WhatsApp 联系方式</Title>
            <Paragraph type="secondary">
              配置 WhatsApp 联系方式，支持多个账号。格式：https://wa.me/电话号码
            </Paragraph>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddWhatsapp}
            >
              添加 WhatsApp
            </Button>
          </Col>
        </Row>

        <Table
          columns={whatsappColumns}
          dataSource={whatsappList}
          rowKey={(record, index) => index}
          pagination={false}
          size="small"
          locale={{ emptyText: '暂无 WhatsApp 联系方式' }}
        />

        <Divider />

        {/* Telegram 配置 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5}>Telegram 联系方式</Title>
            <Paragraph type="secondary">
              配置 Telegram 联系方式，支持多个账号。格式：https://t.me/用户名
            </Paragraph>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTelegram}
            >
              添加 Telegram
            </Button>
          </Col>
        </Row>

        <Table
          columns={telegramColumns}
          dataSource={telegramList}
          rowKey={(record, index) => index}
          pagination={false}
          size="small"
          locale={{ emptyText: '暂无 Telegram 联系方式' }}
        />
      </Form>

      {/* WhatsApp 编辑弹窗 */}
      <Modal
        title={editingWhatsapp ? '编辑 WhatsApp' : '添加 WhatsApp'}
        open={whatsappModalVisible}
        onCancel={() => setWhatsappModalVisible(false)}
        onOk={() => contactForm.submit()}
        width={600}
      >
        <Form
          form={contactForm}
          layout="vertical"
          onFinish={handleSaveWhatsapp}
        >
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入名称' }]}
            extra="例如：客服1、官方客服"
          >
            <Input placeholder="请输入名称" />
          </Form.Item>

          <Form.Item
            label="WhatsApp 链接"
            name="url"
            rules={[
              { required: true, message: '请输入 WhatsApp 链接' },
              { type: 'url', message: '请输入有效的 URL' },
            ]}
            extra="格式：https://wa.me/8613800138000"
          >
            <Input placeholder="https://wa.me/8613800138000" />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch 
              checkedChildren="启用" 
              unCheckedChildren="禁用"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Telegram 编辑弹窗 */}
      <Modal
        title={editingTelegram ? '编辑 Telegram' : '添加 Telegram'}
        open={telegramModalVisible}
        onCancel={() => setTelegramModalVisible(false)}
        onOk={() => contactForm.submit()}
        width={600}
      >
        <Form
          form={contactForm}
          layout="vertical"
          onFinish={handleSaveTelegram}
        >
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入名称' }]}
            extra="例如：客服1、官方客服"
          >
            <Input placeholder="请输入名称" />
          </Form.Item>

          <Form.Item
            label="Telegram 链接"
            name="url"
            rules={[
              { required: true, message: '请输入 Telegram 链接' },
              { type: 'url', message: '请输入有效的 URL' },
            ]}
            extra="格式：https://t.me/username 或 tg://resolve?domain=username"
          >
            <Input placeholder="https://t.me/username" />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch 
              checkedChildren="启用" 
              unCheckedChildren="禁用"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CustomerServiceConfig;
